import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { InputComponent } from '@libs/front/components/input/input.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable';
import { BlastRow, ColorEntry, DrillingSession, ShiftConfig } from 'src/app/shared/models/drilling.models';
import { hslFromString } from 'src/app/utils/color.utils';
import { ActivityCardField, ActivityItem, ActivityItemClick, DrillColorBy, TimeScale } from '../../../planning.types';
import { ActivityCardComponent } from '../../../ui/activity-card/activity-card.component';
import { ActivityCellComponent } from '../../../ui/activity-cell/activity-cell.component';
import { ProgressRingComponent } from '../../../ui/progress-ring/progress-ring.component';
import { ShiftIconComponent } from '../../../ui/shift-icon/shift-icon.component';
import { CARD_POSITIONS, formatDisplayDate, matchesSearch } from '../../../utils/grid-helpers';
import { DayGroup, ShiftColumn, generateColumns, generateDayGroups } from '../../../utils/column-generator';

interface BlastTotals {
  realized: number;
  progress: number;
}

/**
 * Grille de planning du forage : une ligne par tir, une colonne par shift.
 * Presentational — toute la période et les filtres viennent du parent.
 */
@Component({
  selector: 'app-drill-grid',
  standalone: true,
  templateUrl: './drill-grid.component.html',
  styleUrls: ['../../../ui/planning-grid.scss'],
  imports: [
    FormsModule,
    OverlayModule,
    TranslateModule,
    InputComponent,
    ScrollableComponent,
    ActivityCardComponent,
    ActivityCellComponent,
    ProgressRingComponent,
    ShiftIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrillGridComponent {
  readonly blasts      = input<BlastRow[]>([]);
  readonly shiftConfig = input.required<ShiftConfig>();
  readonly startDate   = input.required<Date>();
  readonly endDate     = input.required<Date>();
  readonly timeScale   = input<TimeScale>('week');
  readonly colorBy     = input<DrillColorBy>('rig');
  readonly palette     = input<ColorEntry[]>([]);

  readonly blastClick   = output<BlastRow>();
  readonly sessionClick = output<DrillingSession>();

  protected readonly el      = inject(ElementRef);
  private readonly translate = inject(TranslateService);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  protected readonly searchTerm   = signal('');
  protected readonly activeId     = signal<string | null>(null);
  protected readonly cardAnchor   = signal<HTMLElement | null>(null);
  protected readonly cardPositions: ConnectedPosition[] = CARD_POSITIONS;

  constructor() {
    // Le passage en vue mensuelle recentre la grille sur aujourd'hui.
    effect(() => {
      const scale = this.timeScale();
      untracked(() => {
        if (scale === 'month') {
          setTimeout(() => this.scrollToToday());
        }
      });
    });
  }

  protected readonly shiftColumns = computed<ShiftColumn[]>(() =>
    generateColumns(this.startDate(), this.endDate(), this.shiftConfig()),
  );

  protected readonly dayGroups = computed<DayGroup[]>(() =>
    generateDayGroups(this.shiftColumns(), this.timeScale(), this.lang() || undefined),
  );

  protected readonly cellWidthPx = computed<number>(() =>
    this.timeScale() === 'month' ? 32 : 57,
  );

  protected readonly visibleBlasts = computed<BlastRow[]>(() => {
    const term = this.searchTerm();
    return this.blasts().filter(blast => matchesSearch(term, blast.label, blast.zone));
  });

  /**
   * Blocs indexés par tir et par colonne. Sans cet index, chaque cellule
   * reparcourt toutes les sessions du tir à chaque cycle de détection.
   */
  private readonly itemsByBlastAndColumn = computed(() => {
    const colorBy = this.colorBy();
    const palette = this.palette();
    const index   = new Map<number, Map<string, ActivityItem[]>>();

    for (const blast of this.blasts()) {
      const byColumn = new Map<string, ActivityItem[]>();
      const sorted   = [...blast.sessions].sort((a, b) => a.equipmentLabel.localeCompare(b.equipmentLabel));

      for (const session of sorted) {
        const id    = colorBy === 'rig' ? session.equipmentId    : session.operatorId;
        const label = colorBy === 'rig' ? session.equipmentLabel : session.operatorLabel;
        const item: ActivityItem = {
          id:    session.id,
          value: `${DrillGridComponent.roundMeters(session.drilledMeters)}m`,
          label,
          color: palette.find(e => e.id === id)?.color ?? hslFromString(label),
          date:  session.date,
        };

        const key  = `${session.date}_${session.shiftIndex}`;
        const list = byColumn.get(key);
        if (list) { list.push(item); } else { byColumn.set(key, [item]); }
      }
      index.set(blast.blastId, byColumn);
    }
    return index;
  });

  protected getItemsForCell(blast: BlastRow, col: ShiftColumn): ActivityItem[] {
    return this.itemsByBlastAndColumn().get(blast.blastId)?.get(col.id) ?? [];
  }

  /** La pastille de tir se pose sur le premier shift du jour de tir. */
  protected isBlastDateCell(blast: BlastRow, col: ShiftColumn): boolean {
    return !!blast.blastDate && blast.blastDate === col.date && col.shiftIndex === 0;
  }

  /**
   * Les mètres forés se cumulent en virgule flottante : un total pourtant exact
   * comme 4564,4 s'affiche 4564.400000000001 s'il n'est pas arrondi.
   */
  private static roundMeters(value: number): number {
    return Math.round(value * 10) / 10;
  }

  /** Total foré par colonne, sur les tirs affichés. */
  private readonly shiftTotals = computed(() => {
    const totals = new Map<string, number>();
    for (const blast of this.visibleBlasts()) {
      for (const session of blast.sessions) {
        const key = `${session.date}_${session.shiftIndex}`;
        totals.set(key, (totals.get(key) ?? 0) + session.drilledMeters);
      }
    }
    for (const [key, value] of totals) {
      totals.set(key, DrillGridComponent.roundMeters(value));
    }
    return totals;
  });

  protected getShiftTotal(col: ShiftColumn): number {
    return this.shiftTotals().get(col.id) ?? 0;
  }

  private readonly blastTotals = computed(() => {
    const totals = new Map<number, BlastTotals>();
    for (const blast of this.blasts()) {
      const realized = DrillGridComponent.roundMeters(
        blast.sessions.reduce((sum, s) => sum + s.drilledMeters, 0),
      );
      totals.set(blast.blastId, {
        realized,
        progress: blast.targetMeters ? (realized / blast.targetMeters) * 100 : 0,
      });
    }
    return totals;
  });

  protected getTotals(blast: BlastRow): BlastTotals {
    return this.blastTotals().get(blast.blastId) ?? { realized: 0, progress: 0 };
  }

  /** Borne un pourcentage à [0, 100] pour la largeur CSS de la barre. */
  protected clamp100(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  /** Avancement global des tirs affichés : réalisé cumulé sur cible cumulée. */
  protected readonly overall = computed(() => {
    let realized = 0;
    let target   = 0;
    for (const blast of this.visibleBlasts()) {
      realized += this.getTotals(blast).realized;
      target   += blast.targetMeters ?? 0;
    }
    realized = DrillGridComponent.roundMeters(realized);
    return {
      realized,
      target:  DrillGridComponent.roundMeters(target),
      percent: target ? (realized / target) * 100 : 0,
    };
  });

  private readonly sessionById = computed(() => {
    const index = new Map<string, DrillingSession>();
    for (const blast of this.blasts()) {
      for (const session of blast.sessions) { index.set(session.id, session); }
    }
    return index;
  });

  protected readonly activeSession = computed<DrillingSession | null>(() => {
    const id = this.activeId();
    return id ? this.sessionById().get(id) ?? null : null;
  });

  protected readonly cardFields = computed<ActivityCardField[]>(() => {
    const session = this.activeSession();
    if (!session) { return []; }
    return [
      { label: this.translate.instant('PLANNING.DATE'),           value: formatDisplayDate(session.date) },
      { label: this.translate.instant('PLANNING.DRILLED_LENGTH'), value: `${DrillGridComponent.roundMeters(session.drilledMeters)}m` },
      { label: this.translate.instant('PLANNING.OPERATOR'),       value: session.operatorLabel },
    ];
  });

  protected openCard(click: ActivityItemClick): void {
    this.cardAnchor.set(click.anchor);
    this.activeId.set(click.id);
  }

  protected closeCard(): void {
    this.activeId.set(null);
  }

  protected onSeeMore(): void {
    const session = this.activeSession();
    this.closeCard();
    if (session) { this.sessionClick.emit(session); }
  }

  /** Centre horizontalement la colonne du jour dans la zone visible. */
  private scrollToToday(): void {
    const host    = this.el.nativeElement as HTMLElement;
    const wrapper = host.querySelector('.planning-grid__scroll .scrollable-content') as HTMLElement | null;
    if (!wrapper) { return; }

    const todayCell = wrapper.querySelector('tbody td.is-today') as HTMLElement | null;
    if (!todayCell) { return; }

    const sidebarWidth = (host.querySelector('.planning-grid__sticky-left') as HTMLElement | null)?.offsetWidth ?? 168;
    const visibleWidth = wrapper.clientWidth - sidebarWidth;
    const scrollLeft   = todayCell.offsetLeft - sidebarWidth - visibleWidth / 2 + todayCell.offsetWidth / 2;

    wrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }
}
